// Generated from b2bservices/src/common/productRequirements/rules.ts.
// Do not edit. Run yarn requirements:sync in a paired checkout.

import { isBlank, VITAMIN_MINERAL_FIELDS } from './validators';
import { phraseFillsComplete } from './clpPhrases';
import {
  isBakeryProducts,
  isBeverages,
  isCereals,
  isChemical,
  isCmrHazardCategory,
  isComplementarySupplements,
  isConfectionery,
  isConsumptionReadySnacks,
  isCosmetic,
  isDetergent,
  isElectric,
  isElectricBattery,
  isFats,
  isFeedCategory,
  isFish,
  isFoodSupplements,
  isFruktOrGreen,
  isGoods,
  isIceCream,
  isJewelry,
  isMeat,
  isMedical,
  isMilk,
  isNewFood,
  isNewNonFood,
  isOTC,
  isOtherFoodstuffs,
  isPharma,
  isSpecialFoodNutrition,
  isSpicesSoupsSalads,
  isSunscreen,
  isSweet,
  isToy,
} from './categories';
import {
  requireBooleanValidator,
  requireEnumValidator,
  requireNumberValidator,
  requireStringValidator,
} from './validators';
import type { ProductFieldRule as ProductFieldRuleType, ProductValues, CategoryNode } from './types';
import type { CategoryPredicate } from './categories';

type ConditionParams = { state?: ProductValues; rootCategory?: CategoryNode; categoryPath?: readonly string[] };

const anyCategory =
  (...predicates: CategoryPredicate[]) =>
  ({ state, rootCategory, categoryPath }: ConditionParams) =>
    predicates.some(predicate => !!predicate(state?.foodlaCategory, rootCategory, state, categoryPath));

const isNotStore = ({ state }: ConditionParams) => !state?.producerIsStore;

const isStorePackActive = (state?: ProductValues) => !!(state?.storePackOrderableUnit || state?.storePackDespatchUnit);
const isTransportActive = (state?: ProductValues) => !!(state?.transportOrderableUnit || state?.transportDespatchUnit);
const isPalletActive = (state?: ProductValues) => !!(state?.palletOrderableUnit || state?.palletDespatchUnit);

// Same categories as the form uses to show the field (ProductUpdateForm/index.js).
const showsCountryOfManufacturing = anyCategory(
  isNewNonFood,
  isFish,
  isSpecialFoodNutrition,
  isBeverages,
  isFoodSupplements,
  isFruktOrGreen,
  isOtherFoodstuffs,
  isMeat,
  isBakeryProducts,
  isMilk,
  isCereals,
  isConfectionery,
  isConsumptionReadySnacks,
  isSweet,
  isFats,
  isSpicesSoupsSalads,
  isIceCream
);

// Same conditions as HazardFields (fields/hazard-fields.tsx), rendered for non-food except feed.
const showsHazardPhrases = (params: ConditionParams) => {
  const { state } = params;
  if (!anyCategory(isNewNonFood)(params) || anyCategory(isFeedCategory)(params)) return false;
  const shouldShowFields =
    anyCategory(isPharma, isCmrHazardCategory)(params) && !anyCategory(isChemical, isToy)(params);
  const shouldShowSignalFields =
    anyCategory(isToy, isChemical)(params) &&
    ((state?.signalWords as string) === 'WARNING' || (state?.signalWords as string) === 'DANGER');
  return shouldShowFields || shouldShowSignalFields;
};

const logisticQuestionsExpandTreeViews = [{ treeViewId: 'logistic_questions', nodeId: 'root' }];

/**
 * FC-1950: fields that must be filled before a product of a retailer-activated producer can be activated.
 * A rule never requires a field the form hides for the same product.
 */
export const PRODUCT_FIELD_RULES: ProductFieldRuleType[] = [
  // --- Apotea, all categories ---
  {
    fields: ['foodlaCategory'],
    label: 'Kategori',
    organizations: ['apotea'],
    requireValidator: ({ state }) => !isBlank(state?.foodlaCategory?.id),
  },
  {
    fields: ['short_text'],
    label: 'Produktbeskrivning',
    organizations: ['apotea'],
    requireValidator: requireStringValidator,
  },
  {
    fields: ['shortSalesDescription'],
    label: 'Kort säljande beskrivning',
    organizations: ['apotea'],
    requireValidator: requireStringValidator,
  },
  {
    fields: ['search_terms'],
    label: 'Sökbegrepp och nyckelord',
    organizations: ['apotea'],
    requireValidator: requireStringValidator,
  },
  {
    // The form shows brand_food for food and brand otherwise.
    fields: ['brand_food', 'brand'],
    label: 'Varumärke',
    organizations: ['apotea'],
    categoryCondition: ({ field, state, rootCategory, categoryPath }) => {
      const isFood = isNewFood(state?.foodlaCategory, rootCategory, state, categoryPath);
      return field === 'brand_food' ? isFood : !isFood;
    },
    requireValidator: requireStringValidator,
  },
  {
    fields: ['nameNordicDistributor'],
    label: 'Namn på nordisk distributör',
    organizations: ['apotea'],
    requireValidator: requireStringValidator,
  },
  // Konsumentkontakt is not required for Apotea for now (decided with Apotea, Simon 2026-10-02).
  {
    fields: ['apoteaListPrice', 'apoteaPurchasePrice', 'apoteaStorePrice', 'apoteaStorePriceNok'],
    label: 'Apotea-priser',
    organizations: ['apotea'],
    requireValidator: params => requireNumberValidator(params, { greaterThanZero: true }),
  },
  {
    fields: ['commodityCode'],
    // Varukod sits in the logistics section, which can be collapsed.
    expandTreeViews: logisticQuestionsExpandTreeViews,
    label: 'Varukod',
    organizations: ['apotea'],
    requireValidator: requireStringValidator,
  },
  {
    fields: ['basicDataTaxClassification'],
    label: 'Skattesats',
    organizations: ['apotea'],
    requireValidator: requireNumberValidator,
  },
  {
    fields: ['purchasingDataSupplierArticleNo'],
    label: 'Leverantörens artikelnummer',
    organizations: ['apotea'],
    categoryCondition: isNotStore,
    requireValidator: requireStringValidator,
  },
  {
    // FC-1945: validates the files that remain after saving, so removing the last upload blocks activation
    // unless a replacement is added.
    fields: ['recognizeImages'],
    label: 'Bilder & dokument med produktinformation',
    organizations: ['apotea'],
    requireValidator: ({ state, productInfoFilesCount }) =>
      (productInfoFilesCount ?? (state?.recognizeImages || []).length) > 0,
  },

  // --- Apotea, category-gated ---
  {
    fields: ['ingredient_statement'],
    label: 'Ingrediensförteckning',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isNewFood),
    requireValidator: requireStringValidator,
  },
  // Innehållsförteckning (non_food_ingredients), Näringsdeklaration per 100 g and Användningsinstruktioner
  // are not Apotea-mandatory (decided with Apotea, Simon 2026-10-05).
  {
    // Kosttillskott declare per recommended daily dose (direktiv 2002/46/EG, LIVSFS 2023:3).
    // The dose fields only appear once the basis is set to DAGSDOS, so the basis itself is part of the group.
    fields: ['nutritional_unit', 'dagsdosAmount', 'dagsdosUnit', 'vitamins_minerals'],
    expandTreeViews: [
      { treeViewId: 'nutrition', nodeId: 'root', forFields: ['nutritional_unit', 'dagsdosAmount', 'dagsdosUnit'] },
      { treeViewId: 'vitamins_minerals', nodeId: 'root', forFields: ['vitamins_minerals'] },
    ],
    label: 'Näringsdeklaration per dagsdos',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isFoodSupplements),
    requireValidator: params => {
      const { field, state } = params;
      if (field === 'nutritional_unit') return state?.nutritional_unit === 'DAGSDOS';
      if (field === 'dagsdosAmount') return requireNumberValidator(params);
      if (field === 'dagsdosUnit') return requireStringValidator(params);

      const hasVitaminOrMineral = VITAMIN_MINERAL_FIELDS.some(field => requireNumberValidator({ state, field }));
      const hasOtherSubstance = !!state?.other_substances?.some(
        (item: { name?: unknown; amount?: unknown; unit?: unknown }) => !isBlank(item.name) && !isBlank(item.amount) && !isBlank(item.unit)
      );
      return hasVitaminOrMineral || hasOtherSubstance;
    },
  },
  {
    fields: ['preparedForm'],
    label: 'Beredningsform',
    message: "Välj en beredningsform eller 'Ej tillämpbar'.",
    organizations: ['apotea'],
    categoryCondition: anyCategory(
      isPharma,
      isDetergent,
      isComplementarySupplements,
      isSpecialFoodNutrition,
      isFoodSupplements
    ),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['country_of_manufacturing'],
    label: 'Tillverkningsland',
    organizations: ['apotea'],
    categoryCondition: showsCountryOfManufacturing,
    requireValidator: requireNumberValidator,
  },
  {
    fields: ['sunProtectionFactor'],
    label: 'Solskyddsfaktor (SPF)',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isSunscreen),
    requireValidator: requireEnumValidator,
  },

  // --- Apotea quality questions ---
  {
    fields: ['compliancePpwrPackaging'],
    label: 'Förpackningskrav enligt PPWR (EU) 2025/40',
    organizations: ['apotea'],
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrFoodHygiene'],
    label: 'Livsmedelshygien (EG) 852/2004',
    organizations: ['apotea'],
    categoryCondition: params => isNotStore(params) && anyCategory(isNewFood)(params),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrNutritionClaimsCode'],
    label: 'Näringspåståenden (EG) 1924/2006',
    organizations: ['apotea'],
    categoryCondition: params => isNotStore(params) && anyCategory(isNewFood)(params),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['qrHealthClaimsCode'],
    label: 'Hälsopåståenden (EG) 1924/2006',
    organizations: ['apotea'],
    categoryCondition: params => isNotStore(params) && anyCategory(isNewFood)(params),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['qrFoodLabeling'],
    label: 'Livsmedelsinformation (EU) 1169/2011',
    organizations: ['apotea'],
    categoryCondition: params => isNotStore(params) && anyCategory(isNewFood)(params),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrMisleadingSalesTerms'],
    label: 'Vilseledande säljande uttryck',
    organizations: ['apotea'],
    categoryCondition: params => isNotStore(params) && anyCategory(isNewFood, isFeedCategory)(params),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrClassificationLabelingPackaging'],
    label: 'Klassificering, märkning och förpackning (CLP)',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isChemical, isDetergent),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['complianceReach19072006'],
    label: 'REACH (EG) 1907/2006',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isChemical, isDetergent),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['complianceBiocid'],
    label: 'Biocidprodukt (EU) 528/2012',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isChemical, isDetergent),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['complianceToyDirective'],
    label: 'Leksaksdirektivet (EG) 2009/48',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isElectric),
    requireValidator: requireEnumValidator,
  },
  {
    // Ticket scope; quality-review-fields must show the question for all of these categories.
    fields: ['containsBannedSubstancesPops'],
    label: 'Förbjudna ämnen enligt POPs (EU) 2019/1021',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric, isMedical, isChemical, isDetergent),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['complianceProductSafety2004451'],
    label: 'Produktsäkerhetslagen 2004:451',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['complianceBiocidalTreated'],
    label: 'Biocidbehandlad vara (EU) 528/2012',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['qrElectronicCode'],
    label: 'RoHS-direktivet EU/2011/65',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isElectric, isMedical),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['confirmGuidanceSwedishElectronics'],
    label: 'Svensk bruksanvisning för elektronik',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isElectric, isMedical),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['complianceMetalsSkinContact'],
    label: 'Tungmetaller vid hudkontakt (REACH bilaga XVII)',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isJewelry),
    requireValidator: requireEnumValidator,
  },
  {
    fields: ['complianceMedicalTechProducts'],
    label: 'Medicinteknik (EU) 2017/745 eller 2017/746',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isMedical),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['complianceHaccp'],
    label: 'HACCP för foder',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isFeedCategory),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrAdditivesInFeed'],
    label: 'Fodertillsatser (EG) 1831/2003',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isFeedCategory),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrFeedLabeling'],
    label: 'Fodermärkning (FEDIAF)',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isFeedCategory),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrRelevantFeedCertifications'],
    label: 'Hållbarhetscertifiering för foder',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isFeedCategory),
    requireValidator: requireBooleanValidator,
  },

  // --- Apotea follow-up fields: required only when their trigger applies and the field is shown ---
  {
    // fields/cmr-fields.tsx, shown via showCmrFields in quality-review-fields.tsx.
    fields: ['CMRSubstances'],
    label: 'CMR-klassat ämne',
    organizations: ['apotea'],
    categoryCondition: params =>
      params.state?.cmrSubstance === true &&
      isNotStore(params) &&
      !anyCategory(isNewFood)(params) &&
      anyCategory(isPharma, isCmrHazardCategory)(params),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['specifyCEStandards'],
    label: 'Standarder/krav för CE-märkning',
    organizations: ['apotea'],
    categoryCondition: params =>
      params.state?.ceMarking === true && anyCategory(isGoods, isJewelry, isToy, isElectric, isMedical)(params),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['regulatedSubstance'],
    label: 'Tillståndspliktiga ämnen (REACH bilaga XIV)',
    organizations: ['apotea'],
    categoryCondition: params =>
      params.state?.reachSubstancesAnnex19072006 === true && anyCategory(isToy, isChemical, isDetergent)(params),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['informationHazardousSubstances'],
    label: 'Särskilt farliga ämnen och information för säker användning',
    organizations: ['apotea'],
    categoryCondition: params =>
      params.state?.hazardousSubstancesOver1 === true && anyCategory(isGoods, isJewelry, isToy, isElectric)(params),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['batteryTypeOther'],
    label: 'Annan batterityp',
    organizations: ['apotea'],
    categoryCondition: params =>
      params.state?.batteryType === 'OTHER' && anyCategory(isElectricBattery)(params),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['flammableSubtype'],
    label: 'Typ av brandfarlighet',
    organizations: ['apotea'],
    categoryCondition: params =>
      showsHazardPhrases(params) && !!params.state?.hazardSymbols?.includes('FLAMMABLE'),
    requireValidator: requireEnumValidator,
  },
  {
    // Text slots marked "*" under a selected hazard statement; optional slots can stay empty.
    fields: ['hazardStatementFills'],
    label: 'Faroangivelser (obligatoriska fält)',
    organizations: ['apotea'],
    categoryCondition: showsHazardPhrases,
    requireValidator: ({ state }) => phraseFillsComplete(state?.hazardStatements, state?.hazardStatementFills),
  },
  {
    fields: ['securityNoticeFills'],
    label: 'Skyddsangivelser (obligatoriska fält)',
    organizations: ['apotea'],
    categoryCondition: showsHazardPhrases,
    requireValidator: ({ state }) => phraseFillsComplete(state?.securityNotices, state?.securityNoticeFills),
  },

  // --- Apotea logistics: not for store producers, only for active packaging levels ---
  {
    fields: ['consumer_size_to_order_size_ratio'],
    expandTreeViews: logisticQuestionsExpandTreeViews,
    label: 'B-packstorlek',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore && isStorePackActive(state),
    requireValidator: requireNumberValidator,
  },
  {
    fields: ['transportQtyOfBaseItem'],
    expandTreeViews: logisticQuestionsExpandTreeViews,
    label: 'Antal konsumentförpackningar per TFP',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore && isTransportActive(state),
    requireValidator: requireNumberValidator,
  },
  {
    // LAV is a layer on the pallet, so it follows the pallet level.
    fields: ['lavQuantity'],
    expandTreeViews: logisticQuestionsExpandTreeViews,
    label: 'Antal konsumentförpackningar per LAV',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore && isPalletActive(state),
    requireValidator: requireNumberValidator,
  },
  {
    fields: ['palletQtyOfBaseItem'],
    expandTreeViews: logisticQuestionsExpandTreeViews,
    label: 'Antal konsumentförpackningar per pall',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore && isPalletActive(state),
    requireValidator: requireNumberValidator,
  },
  {
    fields: ['complianceEg12232009'],
    label: 'Kosmetikaförordningen (EG) 1223/2009',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isCosmetic, isSunscreen),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['complianceSunscreenRegulation'],
    label: 'Uppfyller produkten som är eller innehåller solskyddsmedel de preciserade reglerna?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isCosmetic, isSunscreen),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['qrLabelingSwedish'],
    label: 'Svenska förordningen 2013:413',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isCosmetic, isSunscreen),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrMedicalClaims'],
    label: 'Är förpackning och marknadsföring fri från medicinska påståenden?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isCosmetic, isSunscreen),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrMarketingClaimsCosmetic'],
    label: 'Technical document on cosmetic claims',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isCosmetic, isSunscreen),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['qrEfsaUlValues'],
    label: 'UL-värden (tolerable upper intake levels) enligt Efsa',
    organizations: ['apotea'],
    categoryCondition: ({ state, rootCategory, categoryPath }) => {
      return !state?.producerIsStore && anyCategory(isFoodSupplements)({ state, rootCategory, categoryPath });
    },
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['compliancePops'],
    label: 'Förordning (EU) 2019/1021 om långlivade organiska föroreningar (POPs)',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric, isMedical, isChemical, isDetergent),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['categoryWeightloss'],
    label: 'Ange kategori om produkten är för viktminskning',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isSpecialFoodNutrition),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['reachSubstancesAnnex19072006'],
    label: 'Bilaga XIV till REACH-förordningen (EC) 1907/2006',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isToy, isChemical, isDetergent),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['hazardousSubstancesOver1'],
    label:
      'Innehåller produkten något ämne på EU:s kandidatlista i en mängd som överstiger 0,1 viktprocent av produktens innehåll?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['ceMarking'],
    label: 'Är produkten CE-märkt?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric, isMedical),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['checkUniqueIdentification'],
    label: 'Har produkten en unik produktidentifiering (UDI)?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isMedical),
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['explanationNoUDI'],
    label: 'Kommentar till eventuella nej-svar',
    organizations: ['apotea'],
    categoryCondition: ({ state, rootCategory, categoryPath }) => {
      return state?.checkUniqueIdentification === false && anyCategory(isMedical)({ state, rootCategory, categoryPath });
    },
    requireValidator: requireStringValidator,
  },
  {
    fields: ['approvalDietFeed'],
    label: 'Är produkten godkänt som dietfoder?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isFeedCategory),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['cmrSubstance'],
    label: 'Innehåller produkten CMR-klassade ämnen?',
    organizations: ['apotea'],
    categoryCondition: ({ state, rootCategory, categoryPath }) => {
      return !state?.producerIsStore && anyCategory(isPharma, isCmrHazardCategory)({ state, rootCategory, categoryPath });
    },
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['reachSubstance'],
    label: 'Innehåller produkten ett ämne som är förbjudet eller begränsat i REACH-förordningen?',
    organizations: ['apotea'],
    categoryCondition: ({ state, rootCategory, categoryPath }) => {
      return (
        !state?.producerIsStore &&
        anyCategory(
          isToy,
          isElectric,
          isJewelry,
          isGoods,
          isMedical,
          isChemical,
          isDetergent,
          isCosmetic,
          isSunscreen
        )({ state, rootCategory, categoryPath })
      );
    },
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['containsBattery'],
    label: 'Innehåller produkten något batteri?',
    organizations: ['apotea'],
    categoryCondition: anyCategory(isGoods, isJewelry, isToy, isElectric, isMedical),
    requireValidator: requireStringValidator,
  },
  {
    fields: ['plasticTrayPackaging'],
    label: 'Levereras produkten i plasttråg?',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore,
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['shrinkWrapPackaging'],
    label: 'Levereras produkten inplastad, i krympplast eller shrinkwrap?',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore,
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['bestBeforeDate'],
    label: 'Har produkten ett bäst före datum?',
    organizations: ['apotea'],
    categoryCondition: ({ state }) => !state?.producerIsStore,
    requireValidator: requireBooleanValidator,
  },
  {
    fields: ['childrenUnder16'],
    label: 'Är produkten avsedd för barn under 16 år?',
    organizations: ['apotea'],
    categoryCondition: ({ state, rootCategory, categoryPath }) => {
      return (
        !state?.producerIsStore &&
        anyCategory(
          isNewFood,
          isToy,
          isElectric,
          isJewelry,
          isGoods,
          isChemical,
          isDetergent,
          isCosmetic,
          isSunscreen,
          isOTC
        )({ state, rootCategory, categoryPath })
      );
    },
    requireValidator: requireBooleanValidator,
  },
];
